import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { CheckCircle2, Loader2, XCircle, Sprout } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PaymentResult = ({ kind }) => {
  const { t } = useLanguage();
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState(kind === "success" ? "checking" : "cancelled");

  useEffect(() => {
    if (kind !== "success" || !sessionId) return;
    let attempts = 0;
    const poll = async () => {
      try {
        const { data } = await axios.get(`${API}/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setStatus("paid");
          return;
        }
      } catch {}
      attempts += 1;
      if (attempts < 10) setTimeout(poll, 2000);
      else setStatus("timeout");
    };
    poll();
  }, [kind, sessionId]);

  const content = {
    checking: { icon: <Loader2 size={40} className="animate-spin text-[#F4A261]" />, title: t("pay_checking_t"), text: t("pay_checking_x") },
    paid: { icon: <CheckCircle2 size={40} className="text-[#F4A261]" />, title: t("pay_paid_t"), text: t("pay_paid_x") },
    timeout: { icon: <CheckCircle2 size={40} className="text-[#F4A261]" />, title: t("pay_timeout_t"), text: t("pay_timeout_x") },
    cancelled: { icon: <XCircle size={40} className="text-[#5C7368]" />, title: t("pay_cancel_t"), text: t("pay_cancel_x") },
  }[status];

  return (
    <main className="min-h-screen flex items-center justify-center px-6 pt-16" data-testid="payment-result-page">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
        className="max-w-md w-full text-center rounded-3xl border border-[#E2E8E4] bg-[#F2F4F0] p-12"
      >
        <div className="flex justify-center mb-6" data-testid="payment-result-icon">{content.icon}</div>
        <h1 className="font-display text-3xl text-[#1A2F24]" data-testid="payment-result-title">{content.title}</h1>
        <p className="mt-4 text-sm leading-relaxed text-[#5C7368]" data-testid="payment-result-text">{content.text}</p>
        <Link to="/" data-testid="payment-result-home-btn"
          className="mt-8 inline-flex items-center gap-2 rounded-full bg-[#1E352A] text-[#FAF9F6] px-8 py-3.5 text-sm tracking-[0.12em] uppercase transition-colors duration-300 hover:bg-[#F4A261] hover:text-white">
          <Sprout size={15} /> {t("pay_home")}
        </Link>
      </motion.div>
    </main>
  );
};

export default PaymentResult;
